import { apiFetch } from './client';
import type { SseEvent } from '@/types/api';

/**
 * SSE consumed via fetch + ReadableStream rather than EventSource, for two
 * reasons: EventSource cannot send an Authorization header, and a fetch stream
 * is interceptable by MSW so the whole notebook flow is testable offline.
 */

interface RawEvent {
  readonly event: string;
  readonly data: string;
}

/** Splits a raw SSE wire chunk stream into dispatched events. */
export function createSseParser(onEvent: (event: RawEvent) => void): (chunk: string) => void {
  let buffer = '';
  let eventName = 'message';
  let dataLines: string[] = [];

  const dispatch = (): void => {
    if (dataLines.length > 0) {
      onEvent({ event: eventName, data: dataLines.join('\n') });
    }
    eventName = 'message';
    dataLines = [];
  };

  return (chunk: string): void => {
    buffer += chunk;
    // Normalise CRLF so a Java backend's line endings do not break framing.
    buffer = buffer.replace(/\r\n/g, '\n');

    let index = buffer.indexOf('\n');
    while (index !== -1) {
      const line = buffer.slice(0, index);
      buffer = buffer.slice(index + 1);

      if (line === '') {
        dispatch();
      } else if (line.startsWith(':')) {
        /* comment / keep-alive */
      } else {
        const colon = line.indexOf(':');
        const field = colon === -1 ? line : line.slice(0, colon);
        let value = colon === -1 ? '' : line.slice(colon + 1);
        if (value.startsWith(' ')) value = value.slice(1);

        if (field === 'event') eventName = value;
        else if (field === 'data') dataLines.push(value);
      }

      index = buffer.indexOf('\n');
    }
  };
}

function parseEvent(raw: RawEvent): SseEvent | null {
  let payload: unknown;
  try {
    payload = JSON.parse(raw.data);
  } catch {
    return null;
  }
  if (typeof payload !== 'object' || payload === null) return null;

  const withType = payload as { type?: unknown };
  const type = typeof withType.type === 'string' ? withType.type : raw.event;

  switch (type) {
    case 'schema':
    case 'batch':
    case 'progress':
    case 'complete':
    case 'error':
      // Server-controlled shape; validated by the discriminant above. The cast is
      // the boundary between untyped JSON and our contract types.
      return { ...(payload as object), type } as SseEvent;
    default:
      return null;
  }
}

export interface StreamOptions {
  readonly signal?: AbortSignal;
}

/**
 * Streams a query's results. Resolves when the server closes the stream.
 * `onEvent` is called in wire order; a `complete` or `error` event is always
 * the last one a well-behaved server sends.
 */
export async function streamQuery(
  queryId: string,
  onEvent: (event: SseEvent) => void,
  options: StreamOptions = {},
): Promise<void> {
  const init: RequestInit = { headers: { Accept: 'text/event-stream' } };
  if (options.signal) init.signal = options.signal;

  const response = await apiFetch(`/queries/${encodeURIComponent(queryId)}/stream`, init);

  if (!response.body) {
    throw new Error('Query stream returned no body.');
  }

  const feed = createSseParser((raw) => {
    const parsed = parseEvent(raw);
    if (parsed) onEvent(parsed);
  });

  const reader = response.body.getReader();
  const decoder = new TextDecoder();

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) feed(decoder.decode(value, { stream: true }));
    }
    feed(decoder.decode());
    // Flush a trailing event that was not terminated by a blank line.
    feed('\n\n');
  } finally {
    reader.releaseLock();
  }
}
